import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import axios from '../utils/axios';
import { refreshAxiosCache } from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
//
import { setSession } from './utils';
import toast from 'react-hot-toast';
import { requestPermission } from '../firebase';
import { getBrowserType } from '../utils/getBrowserType';
import { clearAccountSession } from '../utils/clearAccountSession';
import { loadVendorOnboardingState } from '../utils/loadVendorOnboarding';
import {
  isOnboardingComplete,
  syncOnboardingFlagsFromStore,
} from '../utils/onboardingRoute';
import {
  resolveStoreCountryId,
  syncAccountPhoneCountryMeta,
  syncCountryId,
} from '../utils/getAccountCountryId';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'OTP') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'DELETE_ACCOUNT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  if (action.type === 'UPDATE_USER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      let user = null;
      try {
        user = storageAvailable ? JSON.parse(localStorage.getItem('user') || 'null') : null;
      } catch (_) {
        user = null;
      }

      if (accessToken && user) {
        setSession(accessToken);

        // Store + subscription from the API before any route decision.
        // Cached subscription/username are not treated as onboarding progress.
        const role = user?.role;
        if (role === 'business' || role === 'mall') {
          user = await loadVendorOnboardingState(user);
          if (storageAvailable) {
            localStorage.setItem('user', JSON.stringify(user));
            if (user?.store_id || user?.store?.id) {
              localStorage.setItem('store-id', String(user.store_id || user.store.id));
            }
            const storeCountryId = resolveStoreCountryId(user?.store, { preferNumericId: false });
            if (storeCountryId) {
              syncCountryId(storeCountryId);
            } else if (user?.country_id) {
              syncCountryId(user.country_id);
            }
            syncAccountPhoneCountryMeta(user?.store);
          }
        }

        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);
  // function getBrowserType() {
  //   const ua = navigator.userAgent;

  //   if (/Chrome/i.test(ua)) return 'Chrome';
  //   if (/Edg/i.test(ua)) return 'Edge';
  //   if (/Firefox/i.test(ua)) return 'Firefox';
  //   if (/Safari/i.test(ua)) return 'Safari';
  //   if (/Opera|OPR/i.test(ua)) return 'Opera';
  //   if (/CriOS/i.test(ua)) return 'Chrome';
  //   if (/FxiOS/i.test(ua)) return 'Firefox';
  //   if (/EdgiOS|EdgA?iOS/i.test(ua)) return 'Edge';
  //   if (/OPiOS|Opera/i.test(ua)) return 'Opera';
  //   if (/Safari/i.test(ua) && /Mobile/i.test(ua)) return 'Safari';
  //   return 'Other'; // افتراضي لأي متصفح غير معروف
  // }

  const withDeviceHeaders = useCallback(async (config = {}) => {
    const deviceType = getBrowserType();
    const headers = {
      ...(config.headers || {}),
      'device-type': deviceType,
    };

    // FCM must not block login — requestPermission/getToken can hang in some browsers
    try {
      const fcmToken = await Promise.race([
        requestPermission(),
        new Promise((resolve) => setTimeout(() => resolve(null), 2500)),
      ]);
      if (fcmToken) {
        headers['device-token'] = fcmToken;
      }
    } catch (error) {
      console.warn('FCM token skipped:', error);
    }

    return { ...config, headers };
  }, []);


  const login = useCallback(async (phone, password) => {
    try {
      const config = await withDeviceHeaders();
      const response = await axios.post('/login', { phone, password }, config);
      const { access_token, ...user } = response?.data?.data;

      // Drop previous account's React Query + local session before installing this one
      clearAccountSession({ clearAuth: true });

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('accessToken', access_token);
      setSession(access_token);
      refreshAxiosCache();

      let sessionUser = user;
      if (user?.role === 'business' || user?.role === 'mall') {
        sessionUser = await loadVendorOnboardingState(user);
        localStorage.setItem('user', JSON.stringify(sessionUser));
        if (sessionUser?.store_id || sessionUser?.store?.id) {
          localStorage.setItem('store-id', String(sessionUser.store_id || sessionUser.store.id));
        }
        const storeCountryId = resolveStoreCountryId(sessionUser?.store, { preferNumericId: false });
        if (storeCountryId) {
          syncCountryId(storeCountryId);
        } else if (sessionUser?.country_id) {
          syncCountryId(sessionUser.country_id);
        }
        syncAccountPhoneCountryMeta(sessionUser?.store);
      }

      dispatch({
        type: 'LOGIN',
        payload: {
          user: sessionUser,
        },
      });

      return {
        status: 'success',
        message: response?.data?.message || 'Login successful',
        data: sessionUser
      };
    } catch (error) {
      return {
        status: 'error',
        statusCode: error?.response?.data?.statusCode,
        message: error?.response?.data?.message || 'رقم الهاتف او كلمة المرور غير صحيحة',
        data: error?.response?.data?.data,
      };
    }
  }, [withDeviceHeaders]);


  // REGISTER
  const register = useCallback(async (name, email, phone, password, password_confirmation, role, referral_code, type, country_id) => {

    const response = await axios.post('/register', {
      name,
      email,
      phone,
      password,
      password_confirmation,
      role,
      referral_code,
      type,
      country_id,
    });

    const responseData = response?.data?.data || {};
    // Support common token field shapes from the register API
    const access_token =
      responseData.access_token ||
      responseData.token ||
      responseData?.original?.access_token ||
      null;

    if (access_token) {
      const {
        access_token: _ignoredAccessToken,
        token: _ignoredToken,
        original,
        ...rest
      } = responseData;

      // Prefer flat user fields; merge nested `original` if the API uses that shape
      const user = {
        ...(original && typeof original === 'object' ? original : {}),
        ...rest,
      };
      delete user.access_token;
      delete user.token;

      clearAccountSession({ clearAuth: true });
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('accessToken', access_token);
      setSession(access_token);
      refreshAxiosCache();

      dispatch({
        type: 'REGISTER',
        payload: {
          user,
        },
      });
    }

    return response;
  }, []);

  // createAccountShop
  const createAccountShop = useCallback(async (name, email, phone, password, shop_id) => {

    const response = await axios.post('/vendor/shop/sub-business', {
      name,
      email,
      phone,
      password,
      shop_id
    });

    const { ...data } = response;

    dispatch({
      type: 'REGISTER',
      payload: {
        data,
      },
    });
    return response
  }, []);

  // Reset Password — on success, end any auth session and require a fresh login
  const reset_password = useCallback(async (password, password_confirmation) => {
    const response = await axios.post('/reset-password', {
      password,
      password_confirmation,
    });

    // Do not keep the OTP/reset token as a logged-in session
    try {
      await axios.post('/logout');
    } catch (_) {
      /* local clear below is enough if logout fails */
    }

    setSession(null);
    clearAccountSession({ clearAuth: true });
    dispatch({
      type: 'LOGOUT',
    });

    return response;
  }, []);

  // change Password — on success, end the auth session (tokens only; keep business data on server)
  const change_password = useCallback(async (old_password, new_password, new_password_confirmation) => {
    const response = await axios.post('/profile/change-password', {
      old_password,
      new_password,
      new_password_confirmation
    });

    // Invalidate server session while the old token may still be accepted
    try {
      await axios.post('/logout');
    } catch (_) {
      /* local clear below is enough if logout fails */
    }

    setSession(null);
    clearAccountSession({ clearAuth: true });
    dispatch({
      type: 'LOGOUT',
    });

    return response;
  }, []);

  // Send OTP
  const send_otp = useCallback(async (identifier, type) => {
    try {
      const response = await axios.post('/otp/send-otp', { type, identifier });
      const { ...otp } = response.data;

      dispatch({
        type: 'OTP',
        payload: {
          otp,
        },
      });
      // toast.success(` قم بأدخال الرمز الذي وصلك على الايميل :  ${response?.data?.data?.otp}`,
      //   { duration: 10000 });

      toast.success(response.data.message);
      return response

    } catch (error) {
      toast.error(error?.response?.data?.message);
      console.error("🚀 ~ login error:", error);
      return {
        status: 'error',
        message: error?.response?.data?.message,
      };
    }
  }, []);

  // Send OTP Phone Number
  // const send_otp_phone = useCallback(async (phone) => {
  //   const response = await axios.post('/otp/send-otp-phone', { phone });
  //   const { ...otp } = response.data;

  //   dispatch({
  //     type: 'OTP',
  //     payload: {
  //       otp,
  //     },
  //   });
  //   return response
  // }, []);


  // OTP Resend
  const resend_otp = useCallback(async (type, identifier) => {
    try {
      const response = await axios.post('/otp/resend-otp', {
        type,
        identifier
      });
      // toast.success(` قم بأدخال الرمز الجديد الذي وصلك على الايميل :  ${response?.data?.data?.otp}`,
      //   { duration: 10000 });
      toast.success(response.data.message);
      dispatch({
        type: 'OTP',
        payload: {
          response,
        },
      });
      return response

    } catch (error) {
      toast.error(error?.response?.data?.message,);
    }
    // if (response.data.status === false) {
    //   return response
    // } else {
    // }
  }, []);

  // OTP Verify
  const otp_verify = useCallback(async (otp, type, identifier, update_profile) => {
    const config = await withDeviceHeaders();
    const response = await axios.post('/otp/verify-otp', {
      otp,
      type,
      identifier,
      update_profile
    }, config);
    if (response.data.status === false) {
      return response
    } else {

      const { access_token, ...user } = response?.data?.data;
      clearAccountSession({ clearAuth: true });
      localStorage.setItem('user', JSON.stringify(user));
      setSession(access_token);
      localStorage.setItem('accessToken', access_token);
      refreshAxiosCache();

      dispatch({
        type: 'OTP',
        payload: {
          user,
        },
      });
      return response
    }
  }, [withDeviceHeaders]);

  // LOGOUT
  const logout = useCallback(async () => {
    try {
      await axios.post('/logout');
    } catch (_) {
      /* still clear local session if network/logout fails */
    }
    setSession(null);
    clearAccountSession({ clearAuth: true });
    dispatch({
      type: 'LOGOUT',
    });
  }, []);

  // DELETE ACCOUNT
  const deleteAccount = useCallback(async () => {
    await axios.delete('/users/delete');
    setSession(null);
    clearAccountSession({ clearAuth: true });
    dispatch({
      type: 'DELETE_ACCOUNT',
    });
  }, []);
  const hasRole = useCallback((allowedRoles = []) => {
    if (!state.user?.role) return false;
    return allowedRoles.includes(state.user.role);
  }, [state.user]);

  /**
   * Merge auth user into memory + localStorage (e.g. after createStore username).
   * @param {object|function} patch - partial user or updater(prev) => next
   */
  const updateUser = useCallback((patch) => {
    let current = null;
    try {
      const raw = localStorage.getItem('user');
      if (raw) current = JSON.parse(raw);
    } catch (_) {
      current = null;
    }

    let next;
    if (typeof patch === 'function') {
      next = patch(current);
    } else {
      next = {
        ...(current || {}),
        ...patch,
        store: {
          ...(current?.store && typeof current.store === 'object' ? current.store : {}),
          ...(patch?.store && typeof patch.store === 'object' ? patch.store : {}),
        },
      };
    }

    if (!next) return null;

    try {
      localStorage.setItem('user', JSON.stringify(next));
      if (next.store_id || next.store?.id) {
        localStorage.setItem('store-id', String(next.store_id || next.store.id));
      }
    } catch (_) {
      /* ignore */
    }

    if (next.store) {
      syncOnboardingFlagsFromStore(next.store);
    }

    dispatch({
      type: 'UPDATE_USER',
      payload: { user: next },
    });
    return next;
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      register,
      createAccountShop,
      otp_verify,
      resend_otp,
      send_otp,
      // send_otp_phone,
      reset_password,
      change_password,
      logout,
      deleteAccount,
      hasRole,
      updateUser,
      isOnboardingComplete: isOnboardingComplete(state.user?.store),
    }),
    [state.isAuthenticated,
    state.isInitialized,
    state.user,
      login,
      logout,
      register,
      createAccountShop,
      otp_verify,
      send_otp,
      resend_otp,
      // send_otp_phone,
      reset_password,
      change_password,
      deleteAccount,
      hasRole,
      updateUser,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
